import type { Product, Supplier } from '@amarantos/api-client'
import {
	getGetSupplierQueryOptions,
	getListProductsQueryOptions,
	useGetSupplierSuspense,
	useListProductsSuspense,
} from '@amarantos/api-client'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, MoreVertical, Pencil, Trash2 } from 'lucide-react'
import { useDeferredValue, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog'
import { DeleteButton } from '@/components/delete-button'
import { PageContent, PageHeader, PageTitle } from '@/components/page'
import { PaginationBar } from '@/components/pagination-bar'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { TableActions } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ProductFormDrawer, type ProductFormDrawerState } from '@/features/products/components/product-form-drawer'
import { ProductsFilters } from '@/features/products/components/products-filters'
import { ProductsTable } from '@/features/products/components/products-table'
import { useDeleteProductMutation } from '@/features/products/hooks/use-product-mutations'
import { toListProductsParams } from '@/features/products/utils/search'
import { supplierDetailSearchSchema } from '@/features/suppliers'
import { SupplierFormDrawer, type SupplierFormDrawerState } from '@/features/suppliers/components/supplier-form-drawer'
import { SupplierInfoCard } from '@/features/suppliers/components/supplier-info-card'
import { useDeleteSupplierMutationWithCallback } from '@/features/suppliers/hooks/use-supplier-mutations'
import { useConfirmAction } from '@/hooks/use-confirm-action'
import { getApiErrorMessage } from '@/utils/errors'

export const Route = createFileRoute('/_authenticated/suppliers/$supplierId')({
	validateSearch: supplierDetailSearchSchema,
	params: {
		parse: ({ supplierId }) => ({ supplierId: Number(supplierId) }),
		stringify: ({ supplierId }) => ({ supplierId: String(supplierId) }),
	},
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, params: { supplierId }, deps }) => {
		queryClient.ensureQueryData(getGetSupplierQueryOptions(supplierId))
		queryClient.ensureQueryData(getListProductsQueryOptions(toListProductsParams(deps, supplierId)))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_supplier_details' />
	),
	component: SupplierDetailRoute,
})

function SupplierDetailRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const { supplierId } = Route.useParams()
	const navigate = useNavigate({ from: '/suppliers/$supplierId' })

	const supplierQuery = useGetSupplierSuspense(supplierId)

	const deferredSearch = useDeferredValue(search)
	const isProductsStale = deferredSearch !== search

	const productsQuery = useListProductsSuspense(toListProductsParams(deferredSearch, supplierId))

	const supplier = supplierQuery.data
	const products = productsQuery.data?.data ?? []
	const productsMeta = productsQuery.data?.meta

	const currentProductsPage = search.productsPage
	const totalProductsPages = productsMeta?.total_pages ?? 1

	const deleteSupplier = useConfirmAction<Supplier, { id: number }>({
		mutation: useDeleteSupplierMutationWithCallback(() => {
			navigate({ to: '/suppliers' })
		}),
		toVariables: (s) => ({ id: s.id }),
		formatError: (err) => getApiErrorMessage(err, t('error.delete_supplier')),
	})

	const deleteProduct = useConfirmAction<Product, { id: number }>({
		mutation: useDeleteProductMutation(),
		toVariables: (p) => ({ id: p.id }),
		formatError: (err) => getApiErrorMessage(err, t('error.delete_product')),
	})

	const supplierDrawerState: SupplierFormDrawerState = useMemo(() => {
		if (search.mode === 'edit') {
			return { mode: 'edit' as const, supplierId }
		}
		return { mode: undefined, supplierId: undefined }
	}, [search.mode, supplierId])

	const supplierDrawerOpen = supplierDrawerState.mode !== undefined

	const productFormDrawerState: ProductFormDrawerState = useMemo(() => {
		if (search.productDrawerMode === 'create') {
			return { mode: 'create' as const }
		}
		if (search.productDrawerMode === 'edit' && search.productId) {
			return { mode: 'edit' as const, productId: search.productId }
		}
		return { mode: undefined }
	}, [search.productDrawerMode, search.productId])

	const productFormDrawerOpen = productFormDrawerState.mode !== undefined

	return (
		<>
			<PageHeader breadcrumbs={[{ label: t('nav.suppliers'), to: '/suppliers' }, { label: supplier.company_name }]}>
				<PageTitle
					backButton={
						<Button asChild variant='ghost' size='icon-sm' aria-label={t('common.back')}>
							<Link to='/suppliers'>
								<ChevronLeft />
							</Link>
						</Button>
					}
					actions={
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='outline' size='icon-sm' aria-label={t('suppliers.detail.actions.label')}>
									<MoreVertical />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align='end'>
								<DropdownMenuItem asChild>
									<Link to='.' search={(prev) => ({ ...prev, mode: 'edit' as const })}>
										<Pencil />
										{t('suppliers.edit.button')}
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem variant='destructive' onClick={() => deleteSupplier.request(supplier)}>
									<Trash2 />
									{t('suppliers.delete.button')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					}
				>
					{supplier.company_name}
				</PageTitle>
				<Tabs value={search.tab}>
					<TabsList>
						<TabsTrigger value='products' asChild>
							<Link to='.' search={(prev) => ({ ...prev, tab: 'products' as const })}>
								{t('suppliers.detail.tabs.products')}
							</Link>
						</TabsTrigger>
						<TabsTrigger value='data' asChild>
							<Link to='.' search={(prev) => ({ ...prev, tab: 'data' as const })}>
								{t('suppliers.detail.tabs.data')}
							</Link>
						</TabsTrigger>
					</TabsList>
				</Tabs>
			</PageHeader>
			<PageContent>
				{search.tab === 'data' && <SupplierInfoCard supplier={supplier} />}
				{search.tab === 'products' && (
					<Card>
						<CardHeader className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
							<CardTitle>{t('suppliers.detail.products_title')}</CardTitle>
							<Button asChild>
								<Link
									to='.'
									search={(prev) => ({
										...prev,
										productDrawerMode: 'create' as const,
										productId: undefined,
									})}
								>
									{t('products.create.button')}
								</Link>
							</Button>
						</CardHeader>
						<CardContent className='space-y-4'>
							<ProductsFilters
								onApply={() => {
									navigate({
										search: (prev) => ({ ...prev, productsPage: 1 }),
									})
								}}
							/>

							<ProductsTable
								products={products}
								isStale={isProductsStale}
								actions={(product) => (
									<TableActions>
										<Button asChild variant='outline' size='sm'>
											<Link
												to='.'
												search={(prev) => ({
													...prev,
													productDrawerMode: 'edit' as const,
													productId: product.id,
												})}
											>
												{t('products.edit.button')}
											</Link>
										</Button>
										<DeleteButton onClick={() => deleteProduct.request(product)} />
									</TableActions>
								)}
							/>

							{totalProductsPages > 1 && (
								<PaginationBar
									currentPage={currentProductsPage}
									totalPages={totalProductsPages}
									onPageChange={(page) => navigate({ search: (prev) => ({ ...prev, productsPage: page }) })}
								/>
							)}
						</CardContent>
					</Card>
				)}

				<SupplierFormDrawer
					{...supplierDrawerState}
					open={supplierDrawerOpen}
					onOpenChange={(isOpen) => {
						if (!isOpen) {
							navigate({
								search: (prev) => ({
									...prev,
									mode: undefined,
								}),
							})
						}
					}}
				/>

				<ProductFormDrawer
					{...productFormDrawerState}
					supplierId={supplierId}
					open={productFormDrawerOpen}
					onOpenChange={(isOpen) => {
						if (!isOpen) {
							navigate({
								search: (prev) => ({
									...prev,
									productDrawerMode: undefined,
									productId: undefined,
								}),
							})
						}
					}}
				/>

				<ConfirmDeleteDialog
					{...deleteSupplier.dialogProps}
					title={t('suppliers.delete.title')}
					description={t('suppliers.delete.description', {
						name: deleteSupplier.target?.company_name ?? t('suppliers.delete.fallback_name'),
					})}
				/>

				<ConfirmDeleteDialog
					{...deleteProduct.dialogProps}
					title={t('products.delete.title')}
					description={t('products.delete.description', {
						name: deleteProduct.target?.name ?? t('products.delete.fallback_name'),
					})}
				/>
			</PageContent>
		</>
	)
}
